import {
  createContext, useContext, useEffect, useMemo, useReducer, useRef,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LIVEKIT_WEB_TOKENS } from './mutations';
import {
  connectRoom, disconnectRoom, onRoomEvent, roomSnapshot, sendChat, setAgentVolume,
  setMicEnabled, trySendChat, unlockAudio as unlockRoomAudio,
} from './livekitRoom';
import { screenContextFor } from './screenContext';
import { screenForPath } from '../lib/agent/screens';
import { setAgentRuntime } from './agent/runtime';
import { executeEnvelope } from './agent/executor';
import { cancelAgentRequests } from './agent/requests';
import { installAgentDevtools } from './agent/devtools';
import {
  AGENT_LABELS, envelopeKey, normalizeAgentType, normalizeEnvelope,
} from '../lib/agent/envelope';
import { isApiReport } from '../lib/agent/apiResult';
import { logAgentApp, logAgentIn, logAgentOut } from '../lib/agent/interactionLog';
import { store } from '../store';
import { setAgentPayload } from '../state/agentSlice';
import { debug } from '../lib/debug';

/**
 * Copilot provider — the one conversation behind the chat dock and the drawer.
 *
 * One room, one thread, shared by every surface. The dock and the drawer are
 * two views of the same conversation, so the room, the message list and the
 * voice state live here rather than in each hook: two `useCopilot` calls used
 * to mean two message lists, and with a real backend two agent sessions.
 *
 * HOW THE STATE IS HELD
 *
 * Room events arrive from a module singleton (`livekitRoom.js`), timers fire
 * long after the render that armed them, and the agent's commands run from
 * module code that has no React scope at all. Handlers written as hooks would
 * either re-subscribe on every render or read state captured renders ago —
 * which is exactly how a question gets sent to a room that is already gone.
 *
 * So the conversation is a plain session object created once per provider
 * (`createCopilotSession`). It owns the current state and every timer, is
 * subscribed to the room exactly once per account, and hands each new state to
 * React through a reducer that only stores the latest snapshot. Handlers always
 * read `state` from the session, never from a closure, and nothing is
 * published once the session is closed.
 *
 * WHAT A TURN IS
 *
 * `ask` opens a turn: the user line goes up, the question goes out with the
 * screen context, and the turn waits. It is answered by the first envelope
 * that carries a reply or a command, by a plain `lk.chat` reply, or — when the
 * envelope never arrives — by the agent's spoken transcript after a short
 * grace. It gives up after 90 s with a retry card, never silently. A handoff
 * keeps it waiting: the receiving agent is the one that answers.
 *
 * VOICE
 *
 * The backend ignores raw speech-to-text turns (they lack its chat marker), so
 * a spoken question only reaches the model if the app sends the final
 * transcript back on `lk.chat` itself. The transport hears each utterance
 * twice — the worker's data packet and the agent's transcription stream — and
 * both copies must become ONE question, or the agent answers it twice.
 */

const CopilotContext = createContext(null);
/**
 * The session's actions alone. Their identity never changes, so a page that
 * only publishes facts (`useScreenContext`) is not re-rendered by every caption
 * word and speaking flag the conversation context carries.
 */
const CopilotApiContext = createContext(null);

/* ── Timing ─────────────────────────────────────────────────────────────── */

/** When the wait stops being normal and the UI says so. */
const WAITING_LONG_MS = 10000;
/** When an unanswered question becomes a retry card. */
const TURN_TIMEOUT_MS = 90000;
/** How long a question asked while disconnected waits for the room. */
const QUEUE_TTL_MS = 60000;
/** A connected room with no agent in it for this long gets one fresh token. */
const AGENT_WAIT_MS = 12000;
/**
 * After that one fresh token, how long the room may stay empty before the
 * panel says so and offers Reconnect. Without it the dot stays amber for good.
 */
const AGENT_STALL_MS = 25000;
/**
 * An agent that joined but has not published `lk.agent.state` is still starting
 * its session, and the worker drops any `lk.chat` sent before its session has
 * started. A participant that never publishes a state (a bot that is not a
 * LiveKit Agents worker) is trusted after this long, so the dock cannot hang.
 */
const AGENT_START_GRACE_MS = 15000;
/** How long a token request may go unanswered before it counts as failed and the backoff takes over. */
const MINT_TIMEOUT_MS = 15000;
/** Re-mint schedule after an unexpected disconnect; one entry per attempt. */
const BACKOFF_MS = [3000, 6000, 12000, 24000, 30000, 30000];
/** The same envelope twice inside this window is one delivery, not two. */
const ENVELOPE_DEDUPE_MS = 3000;
/** Two finals with the same words inside this window are one utterance. */
const TRANSCRIPT_DEDUPE_MS = 5000;
/** Utterance ids are remembered longer, since an id never legitimately repeats. */
const TRANSCRIPT_ID_TTL_MS = 60000;
/** A final transcript this soon after the mic went off still counts. */
const MIC_GRACE_MS = 2000;
/** An open mic with nothing happening for this long is switched off. */
const MIC_IDLE_MS = 15000;
/** How long a spoken transcript waits for its envelope before standing in for it. */
const FALLBACK_DELAY_MS = 1500;
/** A stand-in line older than this is history, not the answer being replaced. */
const TAKEOVER_MS = 30000;
/**
 * Speech that starts this soon after a turn's reply went up is that reply being
 * read aloud. Speech that starts later is something new — a greeting after a
 * reconnect, a spoken-only error — and may still need a line of its own.
 */
const REPLY_SPEECH_WINDOW_MS = 10000;
/** Replies remembered for transcript and chat dedupe. */
const RECENT_REPLY_MS = 180000;
const RECENT_REPLY_MAX = 20;
/** A transcript fragment this long found inside a reply is a copy of it. */
const MIN_CONTAINED_CHARS = 12;
/**
 * A report that could not go out waits this long for the room to come back.
 * The worker keeps an agent session through an absence of up to 120 s, so a
 * report held past that would reach an agent with nothing waiting for it.
 */
const REPORT_HOLD_MS = 110000;
/** Reports held at once; past this the oldest is dropped. */
const REPORT_OUTBOX_MAX = 5;
/** A report whose send failed while the room was up is tried again after this long. */
const REPORT_RETRY_MS = 1500;
/** A confirmation card left unanswered expires, so it cannot be approved against a later screen. */
const CONFIRM_TTL_MS = 120000;
/** The longest the turn reads "answering" while an envelope's commands run. */
const ANSWER_GRACE_MS = 2500;
/** A previous account's room is given this long to close before the next one is joined. */
const TEARDOWN_WAIT_MS = 3000;
/** The thread is capped so a tab left open all week does not grow without bound. */
const MAX_MESSAGES = 300;

/* ── Contract and copy ──────────────────────────────────────────────────── */

const DEFAULT_AGENT = 'trading_web';
/** The display name the backend gives the web participant. */
const TOKEN_NAME = 'pms-web';
const SPEAKER_KEY = 'ki.agentSpeaker';
const SPEAKERS = ['auto', 'on', 'off'];
const NEXT_SPEAKER = { auto: 'on', on: 'off', off: 'auto' };
const AGENT_STATES = new Set(['initializing', 'idle', 'listening', 'thinking', 'speaking']);
/**
 * The agent states that prove its session has started. LiveKit Agents writes
 * `lk.agent.state` from inside the room I/O that also registers the `lk.chat`
 * handler, so a present agent without one of these would drop a question.
 */
const USABLE_AGENT_STATES = new Set(['idle', 'listening', 'thinking', 'speaking']);
const NO_CAPTION = Object.freeze({ user: '', agent: '' });

/** Card actions `SystemLine` knows how to render as a button. */
const ACTION_RECONNECT = 'reconnect';
const ACTION_MIC = 'mic';

/** Connection → the older `status` vocabulary some surfaces still read. */
const STATUS_ALIAS = {
  idle: 'connecting',
  minting: 'connecting',
  connecting: 'connecting',
  waiting_agent: 'connected',
  ready: 'connected',
  reconnecting: 'reconnecting',
  retrying: 'reconnecting',
  disconnected: 'disconnected',
  failed: 'failed',
};

const UNAVAILABLE_TEXT =
  'The copilot could not connect. Every number on screen is still live: click any ' +
  'figure for its history, or use the Analysis Studio to run attribution and scenarios.';
const CONNECTING_TEXT =
  'Still connecting — your question will be sent as soon as the assistant is ready.';
const MIC_PAUSED_TEXT = 'Mic paused — tap to talk';
const OVER_BUDGET_TEXT = 'The assistant has reached its usage limit for now.';
const NO_ANSWER_TITLE = 'No answer yet';
const NOT_SENT_TITLE = 'Not sent — the assistant did not connect in time';
const REPLACED_TITLE = 'Not sent — replaced by your newer question';
const LOST_TITLE = 'The connection dropped before the answer arrived';
const AGENT_MISSING_TITLE = 'The assistant left before answering';
/**
 * How recently another tab must have announced a join for a `duplicate_identity`
 * kick to be blamed on it. Its announcement goes out before it asks for a token,
 * so it always lands ahead of the kick it causes.
 */
const ELSEWHERE_WINDOW_MS = 20000;
/** A kick no other tab owns up to is rejoined silently at most once per this long. */
const STRAY_REJOIN_MS = 30000;
const TAB_CHANNEL = 'ki-copilot';
const ELSEWHERE_TEXT =
  'The assistant was opened in another tab or window, so it disconnected here. Reconnect to use it on this page.';

/* ── Helpers ────────────────────────────────────────────────────────────── */

/**
 * An id for a line this app wrote, unique against every other line in the list.
 *
 * The random tail is what makes that true. A plain counter was module state, so
 * a hot reload re-evaluated the module and reset it to zero while React kept
 * the messages it had already keyed — and the next question came back as
 * `local-1` beside the `local-1` still on screen.
 */
const lineId = (kind) => `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** The catalogue key a path resolves to, for the log only — never worth a throw. */
function safeScreenKey(pathname) {
  try {
    return screenForPath(pathname) || null;
  } catch {
    return null;
  }
}

/**
 * Words reduced to what survives the trip through speech: letters and digits,
 * lower case. The spoken transcript of a reply loses its markdown, punctuation
 * and number formatting, so anything stricter would call a copy a new line.
 */
const normText = (s) => String(s ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

/** Whether two normalised lines are the same utterance, one possibly still growing. */
const sameUtterance = (a, b) => Boolean(a && b)
  && (a.startsWith(b) || b.startsWith(a) || (b.length >= MIN_CONTAINED_CHARS && a.includes(b)));

/**
 * Whether spoken words are (part of) a reply already shown. One direction only:
 * speech that merely starts with an earlier short reply ("Done. But the second
 * fund could not be added…") says something that reply did not.
 */
const spokenPartOf = (reply, spoken) => Boolean(reply && spoken)
  && (reply.startsWith(spoken) || (spoken.length >= MIN_CONTAINED_CHARS && reply.includes(spoken)));

function readSpeaker() {
  try {
    const stored = localStorage.getItem(SPEAKER_KEY);
    return SPEAKERS.includes(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

/**
 * The usage-limit text when a token request was refused for budget, else null.
 * The backend raises HTTP 429 from the mutation, which reaches Apollo as a
 * GraphQL error reading "429: <message>". Retrying it would only be refused
 * again, and calling it a connection failure sends people hunting the worker.
 */
function overBudgetMessage(err) {
  const errors = err?.graphQLErrors || err?.errors || [];
  for (const e of errors) {
    const m = /^429:\s*(.*)$/s.exec(String(e?.message ?? ''));
    if (m) return m[1].trim() || OVER_BUDGET_TEXT;
  }
  if (err?.networkError?.statusCode === 429) return OVER_BUDGET_TEXT;
  return null;
}

function writeSpeaker(value) {
  try {
    localStorage.setItem(SPEAKER_KEY, value);
  } catch {
    /* Private mode or storage disabled — the choice still holds for this tab. */
  }
}

/** The name shown for an agent type; unknown types are spelled out from their key. */
function agentLabel(agent) {
  if (!agent) return 'Assistant';
  const known = AGENT_LABELS && AGENT_LABELS[agent];
  if (known) return known;
  const words = String(agent).replace(/_web$/, '').split('_').filter(Boolean);
  return words.map((w) => w[0].toUpperCase() + w.slice(1)).join(' ') || 'Assistant';
}

/**
 * Whether the agent's voice should be heard. `silenced` is `stop()`: it mutes
 * the voice until the next question without changing the reader's setting.
 */
const isAudible = (s) => !s.silenced
  && (s.speaker === 'on' || (s.speaker === 'auto' && s.lastVia === 'voice'));

function safeIsReport(text) {
  try {
    return Boolean(isApiReport(text));
  } catch {
    return false;
  }
}

/** Everything a fresh conversation starts from. The speaker setting is the one thing that outlives it. */
function freshState() {
  return {
    connection: 'idle',
    agentState: null,
    turn: 'idle',
    waitingLong: false,
    activeAgent: DEFAULT_AGENT,
    messages: [],
    mic: 'off',
    speaker: readSpeaker(),
    lastVia: 'text',
    silenced: false,
    canPlayback: true,
    agentActive: false,
    caption: NO_CAPTION,
    /** The room stayed empty after its one fresh token: Reconnect is offered. */
    agentStalled: false,
    /** Confirmation cards still waiting for Run or Cancel. */
    pendingConfirms: 0,
    /** The stock or fund a per-subject agent (stock_research_web, mf_research_web) is minted for. */
    agentSubject: null,
    /** The further token variables that agent is minted with (the MF analyst's `window`), or null. */
    agentOptions: null,
    /** The analyst session the room is attached to — the interactive analysts only. */
    analystSessionId: null,
    /** Bumped on every envelope carrying analyst events; `analystEvents` holds the last batch. */
    analystSeq: 0,
    analystEvents: null,
  };
}

/**
 * The previous account's room, still closing. Module-level because a remount
 * creates a new session while the old one's disconnect is in flight, and the
 * new join must not race it.
 */
let teardown = null;

function settleWithin(promise, ms) {
  let timer = null;
  const cap = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([Promise.resolve(promise).catch(() => {}), cap])
    .finally(() => clearTimeout(timer));
}

/* ── Session ────────────────────────────────────────────────────────────── */

function createCopilotSession() {
  let state = freshState();
  let publish = null;
  let client = null;
  let location = { pathname: '/', search: '' };

  /* Lifecycle. `gen` changes on open and close, so an await that resumes into
     a different session (an account change) can tell and stop. */
  let alive = false;
  let gen = 0;
  let unsubscribe = null;
  let uninstallRuntime = null;
  let uninstallDevtools = null;
  const timers = new Set();

  /* Connection. */
  let minting = null;
  let attempts = 0;
  let retryTimer = null;
  let agentTimer = null;
  let startTimer = null;
  let agentRemintUsed = false;
  let expectingDisconnect = false;
  let halted = false;
  /** What the reader was told when the session halted, repeated if they ask again. */
  let haltText = null;
  /**
   * Another tab or device joined the room as this account and LiveKit closed
   * ours. Only an explicit act (asking, Reconnect) takes the room back — not
   * returning to the tab, or two open tabs would evict each other on every
   * switch between them.
   */
  let displaced = false;
  /** Tells the other tabs of this app when this one joins, so a kick can be attributed. */
  let tabChannel = null;
  let sessionUser = null;
  let otherTabJoinedAt = 0;
  let strayRejoinAt = 0;
  let agentPresent = false;
  let failNoticeShown = false;

  /* Turns. `answering` holds whatever is currently producing an answer — an
     envelope whose commands are running, a chat reply still streaming. */
  let turn = null;
  let queued = null;
  const answering = new Set();

  /* Dedupe. */
  const envelopesSeen = new Map();
  let recentReplies = [];
  const heardIds = new Map();
  let heardTexts = [];
  const fallbackTimers = new Map();
  /** When each agent speech segment was first heard, so its start can be compared with the reply. */
  const segmentStarts = new Map();

  /* Voice. */
  let micIntent = false;
  let micSeq = 0;
  let micOffAt = 0;
  let userActive = false;
  let idleTimer = null;

  const confirms = new Map();
  let facts = null;

  /* Reports waiting for the room: `{ body, resolve, timer }`, oldest first. */
  const outbox = [];
  let outboxTimer = null;
  let flushing = false;

  /* ── Timers ── */

  /** A timeout that dies with the session. */
  const later = (fn, ms) => {
    const id = setTimeout(() => {
      timers.delete(id);
      if (alive) fn();
    }, ms);
    timers.add(id);
    return id;
  };

  const cancel = (id) => {
    if (id != null) {
      clearTimeout(id);
      timers.delete(id);
    }
    return null;
  };

  /* ── State ── */

  const applyVolume = () => {
    try {
      setAgentVolume(isAudible(state) ? 1 : 0);
    } catch (err) {
      debug('copilot · volume not applied', { message: err?.message });
    }
  };

  /**
   * The only way state changes. Unchanged patches publish nothing, so a stream
   * of identical partials does not re-render every consumer. The agent's
   * volume follows `audible` here, synchronously, so `stop()` silences the
   * voice in the same tick rather than a render later.
   */
  const commit = (patch) => {
    if (!alive) return;
    const changed = Object.keys(patch).some((k) => state[k] !== patch[k]);
    if (!changed) return;
    const wasAudible = isAudible(state);
    state = { ...state, ...patch };
    if (isAudible(state) !== wasAudible) applyVolume();
    if (publish) publish(state);
  };

  const setMessages = (list) => {
    commit({ messages: list.length > MAX_MESSAGES ? list.slice(list.length - MAX_MESSAGES) : list });
  };

  const findMessage = (id) => (id ? state.messages.find((m) => m.id === id) || null : null);

  const addMessage = (msg) => {
    const line = msg && typeof msg === 'object' ? msg : {};
    const id = line.id || lineId(line.from || 'sys');
    if (alive) setMessages([...state.messages, { at: Date.now(), ...line, id }]);
    return id;
  };

  /** Patches a line in place. `card` is merged, so a status change keeps the title and items. */
  const updateMessage = (id, patch) => {
    if (!alive || !id || !patch || typeof patch !== 'object') return;
    const at = state.messages.findIndex((m) => m.id === id);
    if (at === -1) return;
    const prev = state.messages[at];
    const next = { ...prev, ...patch, id: prev.id };
    if (patch.card && prev.card) next.card = { ...prev.card, ...patch.card };
    const list = state.messages.slice();
    list[at] = next;
    setMessages(list);
  };

  /** A line from the app itself: a notice, an error, a handoff, a retry card. */
  /**
   * A line the app writes for itself — and never the same one twice running.
   *
   * Connecting fails in more than one place: the backoff gives up, and a fresh
   * token that still finds no agent says the same thing. Each path guards its
   * own notice, so none of them saw the others, and a panel that had been
   * trying for a while showed the identical "could not connect" paragraph five
   * times down the transcript. A notice repeated verbatim with nothing between
   * it and its twin tells the reader nothing the first one did not.
   */
  const say = (kind, text, card) => {
    const last = [...state.messages].reverse().find((m) => m.from === 'sys');
    if (text && last && last.kind === kind && last.text === text) return last.id;
    return addMessage({
      from: 'sys',
      kind,
      ...(text ? { text } : null),
      ...(kind === 'error' ? { ok: false } : null),
      ...(card ? { card } : null),
    });
  };

  const setCaption = (patch) => {
    const next = { ...state.caption, ...patch };
    if (next.user === state.caption.user && next.agent === state.caption.agent) return;
    commit({ caption: next });
  };

  /* ── Context ── */

  /**
   * The frame for one send, built from where the reader is NOW. Page facts are
   * used only when they were published for this pathname: a page's effect can
   * run before or after the route changes, and facts from the page just left
   * would describe the wrong subject.
   */
  const frameFor = (text, { voice = false } = {}) => {
    const pathname = location?.pathname || '/';
    const search = location?.search || '';
    const ui = store.getState()?.ui || {};
    const pageFacts = facts && facts.pathname === pathname ? facts.facts : null;
    let screen = 'Unknown';
    let context = null;
    let failed = null;
    try {
      ({ screen, context } = screenContextFor({
        pathname, search, asOf: ui.asOf, facts: pageFacts,
      }));
    } catch (err) {
      /* A context bug must not cost the reader their question. */
      console.warn('[copilot] screen context failed', err);
      failed = err?.message || String(err);
    }
    const fallback = !context || typeof context !== 'object';
    if (fallback) {
      context = {
        contract: 'pms-agent/1',
        surface: 'pms_web',
        path: pathname,
        ...(ui.asOf ? { asOf: ui.asOf } : null),
      };
    }
    if (voice) context = { ...context, voice: true };
    const frame = { text, screen: screen || 'Unknown', context };
    /* The screen and the context, logged where they are decided rather than
       only as part of the frame: an agent that answers about the wrong thing
       is nearly always a screen name or a `screen_security` that did not come
       out of the route the reader is actually on. `key` is the catalogue entry
       the path resolved to — the step between the URL and the name the agent
       sees — and `facts` names what the page itself contributed. */
    logAgentOut('screen context', {
      screen: frame.screen,
      key: safeScreenKey(pathname),
      path: pathname,
      search: search || '',
      seat: ui.assignedRole || ui.role || null,
      asOf: ui.asOf ?? null,
      voice,
      facts: pageFacts ? Object.keys(pageFacts) : null,
      ...(fallback ? { fallback: true } : null),
      ...(failed ? { error: failed } : null),
      context: frame.context,
    });
    return frame;
  };

  /* ── Connection ── */

  /** Whether the agent in the room has started the session that reads `lk.chat`. */
  const agentUsable = (agentState = state.agentState) => agentPresent && USABLE_AGENT_STATES.has(agentState);

  const setConnection = (next) => {
    if (!alive || state.connection === next) return;
    debug('copilot · connection', { from: state.connection, to: next });
    commit({ connection: next });
    syncAgentWait();
    if (next === 'ready') onReady();
  };

  /**
   * The timers of a joined room that has no usable agent, re-derived whenever
   * the connection or the agent's presence changes. Nobody in the room: wait
   * 12 s, then one fresh token; after that token, wait again and offer
   * Reconnect. An agent still starting: wait for its state, then trust it.
   * Nothing runs while halted — an over-budget worker would only be dispatched
   * again and refused again.
   */
  const syncAgentWait = () => {
    if (state.connection !== 'waiting_agent' || halted) {
      agentTimer = cancel(agentTimer);
      startTimer = cancel(startTimer);
      return;
    }
    if (agentPresent) {
      agentTimer = cancel(agentTimer);
      if (!startTimer) startTimer = later(onAgentSlowStart, AGENT_START_GRACE_MS);
      return;
    }
    startTimer = cancel(startTimer);
    if (!agentTimer) agentTimer = later(onAgentLate, agentRemintUsed ? AGENT_STALL_MS : AGENT_WAIT_MS);
  };

  /**
   * The token mutation, abandoned after `MINT_TIMEOUT_MS`. A backend that
   * accepts the request and never answers would otherwise hold `minting` for
   * good, and every way back — the backoff, Reconnect, returning to the tab —
   * waits for `minting` to clear.
   */
  const requestToken = async (entry, variables) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer = null;
    const timedOut = new Promise((_resolve, reject) => {
      timer = later(() => {
        timer = null;
        controller?.abort();
        const err = new Error('The token request did not answer in time');
        err.code = 'mint_timeout';
        reject(err);
      }, MINT_TIMEOUT_MS);
    });
    try {
      /* `no-cache`: a join token has no business in the Apollo cache, where
         devtools would show it to anyone looking over a shoulder. */
      return await Promise.race([
        client.mutate({
          mutation: entry.document,
          variables,
          fetchPolicy: 'no-cache',
          ...(controller ? { context: { fetchOptions: { signal: controller.signal } } } : null),
        }),
        timedOut,
      ]);
    } finally {
      cancel(timer);
    }
  };

  /**
   * Mint a token for the ACTIVE agent and join its room.
   *
   * Never two at once: a second call while one is in flight is dropped, so a
   * visibility change, a retry timer and a user's Reconnect landing together
   * still cost one token. The mutation is the active agent's, not Trading's —
   * minting through Trading after a handoff would switch the backend back.
   */
  const mint = async (why) => {
    if (!alive || minting) return;
    displaced = false;
    try {
      tabChannel?.postMessage({ type: 'joining', user: sessionUser });
    } catch {
      /* Only attribution is lost. */
    }
    const token = { why };
    const myGen = gen;
    minting = token;
    retryTimer = cancel(retryTimer);
    setConnection('minting');
    try {
      if (teardown) await settleWithin(teardown, TEARDOWN_WAIT_MS);
      if (myGen !== gen) return;
      let agent = state.activeAgent;
      /* A per-stock agent with no stock to open cannot be minted either. */
      if (!LIVEKIT_WEB_TOKENS[agent] || (LIVEKIT_WEB_TOKENS[agent].subject && !state.agentSubject)) {
        /* Minting through Trading switches the backend to Trading, so the header
           says so rather than naming an agent that is no longer answering. */
        debug('copilot · no web token for the active agent; minting Trading', { agent });
        agent = DEFAULT_AGENT;
        commit({ activeAgent: agent, agentSubject: null, agentOptions: null });
      }
      const entry = LIVEKIT_WEB_TOKENS[agent];
      if (!entry?.document || !entry?.field || !client) {
        throw new Error(`No token mutation is available for ${agent}`);
      }
      const { data } = await requestToken(entry, {
        name: TOKEN_NAME,
        ...(entry.subject ? { securitySymbol: state.agentSubject } : null),
        /* Only the variables this agent's mutation declares. */
        ...Object.fromEntries((entry.options || [])
          .filter((k) => state.agentOptions?.[k] != null)
          .map((k) => [k, state.agentOptions[k]])),
      });
      if (myGen !== gen || minting !== token) return;
      const grant = data?.[entry.field];
      if (!grant?.token || !grant?.wsUrl) throw new Error('No LiveKit token was issued');
      const sessionId = entry.subject ? Number(grant.analystSessionId) || null : null;
      commit({ analystSessionId: sessionId });
      settleOpening(sessionId ? { sessionId, symbol: state.agentSubject } : null);
      /* The grant minus its token: which worker, which room, resumed or fresh. */
      debug('copilot → token', {
        why,
        agent,
        room: grant.room,
        worker: grant.agentName,
        thread: grant.threadName,
        newRoom: grant.isNewRoom,
      });
      if (state.connection === 'minting') setConnection('connecting');
      await connectRoom({ token: grant.token, wsUrl: grant.wsUrl });
      if (myGen !== gen) return;
      /* Released before reconciling, so a drop heard during the join (which
         left its retry to this point) can schedule one. */
      if (minting === token) minting = null;
      reconcile();
    } catch (err) {
      if (myGen !== gen) return;
      if (minting === token) minting = null;
      if (err?.code === 'aborted') {
        /* Something left the room on purpose while this was joining; that is
           a decision, not a failure to retry. */
        debug('copilot · connect aborted', { why });
        if (state.connection === 'minting' || state.connection === 'connecting') setConnection('disconnected');
        return;
      }
      failOpening(err);
      const limitText = overBudgetMessage(err);
      if (limitText) {
        setConnection('disconnected');
        onSystem({ type: 'over_budget', message: limitText });
        return;
      }
      console.warn('[copilot] connect failed', err?.message || err);
      scheduleRetry();
    } finally {
      if (minting === token) minting = null;
      /* A move to another agent's room asked for while this one was joining. */
      if (myGen === gen && switchPending && !minting) {
        switchPending = false;
        changeRooms('switch');
      }
    }
  };

  /* ── Per-stock agents ── */

  /** Waiting for the session a per-stock agent's token opens: `{ resolve, reject, timer }`. */
  let opening = null;
  let switchPending = false;

  const settleOpening = (result) => {
    if (!opening || !result) return;
    const o = opening;
    opening = null;
    cancel(o.timer);
    o.resolve(result);
  };
  const failOpening = (err) => {
    if (!opening) return;
    const o = opening;
    opening = null;
    cancel(o.timer);
    o.reject(err instanceof Error ? err : new Error(String(err)));
  };

  /**
   * Leave the room and join the active agent's. A per-stock agent has a room
   * of its own (and a mutation that needs the stock), so moving to or from one
   * is a new token and a new room, not the in-room switch the others make.
   */
  const changeRooms = async (why) => {
    if (!alive) return;
    if (minting) {
      switchPending = true;
      return;
    }
    const myGen = gen;
    /* A turn in flight is left waiting: the agent in the next room answers a
       handoff itself, and the turn's own timeout covers one that never does. */
    expectingDisconnect = true;
    try {
      await disconnectRoom();
    } catch {
      /* Already gone. */
    } finally {
      if (myGen === gen) expectingDisconnect = false;
    }
    if (!alive || myGen !== gen) return;
    commit({ agentState: null, agentActive: false, analystSessionId: null });
    agentPresent = false;
    attempts = 0;
    agentRemintUsed = false;
    mint(why);
  };

  /**
   * Make `agent` the one answering — on `subject` for a per-stock agent. The
   * room changes when either side of the move is per-stock, or the stock
   * changes; between the shared web agents only the active agent is recorded.
   */
  const switchAgent = (agent, subject = null, options = null) => {
    const from = LIVEKIT_WEB_TOKENS[state.activeAgent];
    const to = LIVEKIT_WEB_TOKENS[agent];
    const nextSubject = to?.subject ? subject || null : null;
    if (to?.subject && !nextSubject) return false;
    /* A different window is a different session, so it is a different room too. */
    const nextOptions = to?.options && options ? options : null;
    const sameOptions = JSON.stringify(nextOptions) === JSON.stringify(state.agentOptions ?? null);
    const same = agent === state.activeAgent && nextSubject === state.agentSubject && sameOptions;
    const moving = Boolean(from?.subject || to?.subject) && !same;
    if (same) return false;
    commit({ activeAgent: agent, agentSubject: nextSubject, agentOptions: nextOptions });
    if (moving) changeRooms(`switch:${agent}`);
    return moving;
  };

  /**
   * Open a per-subject research analyst on `symbol` in the assistant — its
   * web token opens (or re-opens) the reader's research session on the stock
   * or fund. Resolves `{ sessionId, symbol }` once the room is joined with it.
   */
  const openResearch = (agent, symbol, options = null) => new Promise((resolve, reject) => {
    const sym = String(symbol || '').trim();
    if (!alive || !sym) {
      reject(new Error(alive ? 'Nothing to open the analyst on.' : 'The assistant is not running.'));
      return;
    }
    failOpening(new Error('Replaced by a newer request.'));
    const sameOptions = JSON.stringify(options ?? null) === JSON.stringify(state.agentOptions ?? null);
    if (state.activeAgent === agent && state.agentSubject === sym && sameOptions && state.analystSessionId
      && (state.connection === 'ready' || state.connection === 'waiting_agent')) {
      resolve({ sessionId: state.analystSessionId, symbol: sym });
      return;
    }
    opening = { resolve, reject, timer: later(() => failOpening(new Error('The research analyst did not answer in time.')), MINT_TIMEOUT_MS * 3) };
    if (!switchAgent(agent, sym, options)) {
      /* Already this agent on this subject, but not joined: join now. */
      halted = false;
      reconnectNow('open');
    }
  });
  /** The stock research analyst on a stock. */
  const openStockResearch = (symbol) => openResearch('stock_research_web', symbol);
  /** The MF research analyst on a fund, for a research window (quarterly when not given). */
  const openMfResearch = (symbol, { window: researchWindow = 'quarterly' } = {}) => (
    openResearch('mf_research_web', symbol, { window: researchWindow || 'quarterly' })
  );

  /** Back to the shared web agents (Trading) from a per-subject one — a stock's or a fund's. */
  const leaveStockResearch = () => {
    if (!alive || !LIVEKIT_WEB_TOKENS[state.activeAgent]?.subject) return;
    failOpening(new Error('Left the research analyst.'));
    switchAgent(DEFAULT_AGENT, null);
  };

  /**
   * After a join resolves, take the room's own word for where things stand.
   * The events normally got there first; this covers a connect that returned
   * the room already joined, which emits nothing.
   */
  const reconcile = () => {
    let snap = null;
    try {
      snap = roomSnapshot();
    } catch {
      snap = null;
    }
    if (!snap) return;
    if (snap.state === 'disconnected') {
      /* The room dropped between joining and here. Its disconnect arrived while
         this mint was still in flight, which defers the retry to the mint. */
      if (halted) setConnection('disconnected');
      else if (state.connection !== 'disconnected' && state.connection !== 'failed') scheduleRetry();
      return;
    }
    if (snap.state !== 'connected') return;
    commit({ canPlayback: snap.canPlaybackAudio !== false });
    if (state.connection === 'ready' || state.connection === 'waiting_agent') return;
    agentPresent = Boolean(snap.agentIdentity);
    setConnection(agentUsable(snap.agentState) ? 'ready' : 'waiting_agent');
  };

  const giveUpConnecting = () => {
    setConnection('failed');
    if (failNoticeShown) return;
    failNoticeShown = true;
    say('error', UNAVAILABLE_TEXT, { actionId: ACTION_RECONNECT });
  };

  const scheduleRetry = () => {
    if (!alive || halted || minting || retryTimer) return;
    if (attempts >= BACKOFF_MS.length) {
      giveUpConnecting();
      return;
    }
    const delay = BACKOFF_MS[attempts];
    attempts += 1;
    /* 'retrying', not 'disconnected': the app is still trying on its own, so
       the panel must not read as given up (red dot, Reconnect) during the wait. */
    setConnection('retrying');
    debug('copilot · retry scheduled', { attempt: attempts, inMs: delay });
    retryTimer = later(() => {
      retryTimer = null;
      mint('retry');
    }, delay);
  };

  /** Reconnect now rather than on the backoff schedule — the reader is here and waiting. */
  const reconnectNow = (why) => {
    if (!alive || halted || minting) return;
    if (state.connection === 'failed') {
      attempts = 0;
      failNoticeShown = false;
    }
    retryTimer = cancel(retryTimer);
    mint(why);
  };

  /**
   * A room with no agent in it: leave it and ask for a fresh token.
   *
   * Leaving first matters. A token for the room we are already in rejoins that
   * same room and changes nothing; only once it is left can the backend find
   * it dead and dispatch an agent into a new one.
   */
  const remintForAgent = async (why) => {
    if (!alive || minting || halted) return;
    agentRemintUsed = true;
    agentTimer = cancel(agentTimer);
    startTimer = cancel(startTimer);
    commit({ agentStalled: false });
    giveUp(turn, AGENT_MISSING_TITLE);
    const myGen = gen;
    expectingDisconnect = true;
    try {
      await disconnectRoom();
    } catch {
      /* Already gone — which is the point. */
    } finally {
      if (myGen === gen) expectingDisconnect = false;
    }
    if (!alive || myGen !== gen) return;
    mint(why);
  };

  const onAgentLate = () => {
    agentTimer = null;
    if (state.connection !== 'waiting_agent' || halted || agentPresent) return;
    if (!agentRemintUsed) {
      debug('copilot · agent did not join; re-minting', { waitedMs: AGENT_WAIT_MS });
      remintForAgent('agent-missing');
      return;
    }
    /* The fresh token brought no agent either. Staying amber with no way on
       would leave only a page reload, so the reader is told and offered one. */
    if (state.agentStalled) return;
    debug('copilot · still no agent after a fresh token', { waitedMs: AGENT_STALL_MS });
    commit({ agentStalled: true });
    say('error', UNAVAILABLE_TEXT, { actionId: ACTION_RECONNECT });
  };

  const onAgentSlowStart = () => {
    startTimer = null;
    if (state.connection !== 'waiting_agent' || halted || !agentPresent) return;
    debug('copilot · agent never published its state; treating it as ready', { waitedMs: AGENT_START_GRACE_MS });
    setConnection('ready');
  };

  /**
   * An over-budget worker left the room. Staying joined to an empty room would
   * start the agent-missing re-mint, and every fresh token dispatches another
   * worker that is refused again; so this side leaves too and waits for the
   * reader's Reconnect.
   */
  const leaveHaltedRoom = async () => {
    const myGen = gen;
    commit({ agentState: null, agentActive: false });
    setConnection('disconnected');
    expectingDisconnect = true;
    try {
      await disconnectRoom();
    } catch {
      /* Already gone. */
    } finally {
      if (myGen === gen) expectingDisconnect = false;
    }
  };

  const onReady = () => {
    attempts = 0;
    agentRemintUsed = false;
    failNoticeShown = false;
    retryTimer = cancel(retryTimer);
    commit({ agentStalled: false });
    if (halted) return;
    flushOutbox();
    flushQueue();
    /* The mic comes back on its own after a drop only if the reader left it on. */
    if (micIntent && state.mic !== 'on' && state.mic !== 'requesting') switchMic(true);
  };

  /* ── Turns ── */

  const syncTurn = () => {
    let next = 'idle';
    if (turn && turn.status === 'awaiting') next = 'awaiting';
    else if (answering.size > 0) next = 'answering';
    commit(next === 'awaiting' ? { turn: next } : { turn: next, waitingLong: false });
  };

  const clearTurnTimers = (t) => {
    if (!t) return;
    t.waitTimer = cancel(t.waitTimer);
    t.deadTimer = cancel(t.deadTimer);
  };

  /** Close a turn that will not be answered, and say so with a way to ask again. */
  const giveUp = (t, title) => {
    if (!t || t.status !== 'awaiting') return;
    clearTurnTimers(t);
    t.status = 'unanswered';
    say('retry', null, { title, question: t.text });
    syncTurn();
  };

  /** A new question supersedes whatever the previous one was still waiting for. */
  const beginTurn = (text, via) => {
    if (turn) {
      clearTurnTimers(turn);
      if (turn.status === 'awaiting') turn.status = 'superseded';
    }
    const t = {
      id: lineId('turn'),
      text,
      via,
      status: 'awaiting',
      startedAt: Date.now(),
      replied: false,
      repliedAt: 0,
      /* The one speech segment taken as this reply read aloud; see `onAgentTranscript`. */
      readSegment: null,
      lineId: null,
      lineKind: null,
      lineAt: 0,
      waitTimer: null,
      deadTimer: null,
    };
    t.waitTimer = later(() => {
      t.waitTimer = null;
      if (turn === t && t.status === 'awaiting') commit({ waitingLong: true });
    }, WAITING_LONG_MS);
    t.deadTimer = later(() => {
      t.deadTimer = null;
      giveUp(t, NO_ANSWER_TITLE);
    }, TURN_TIMEOUT_MS);
    turn = t;
    commit({ waitingLong: false });
    syncTurn();
    noteVoiceActivity();
    return t;
  };

  const answered = (t) => {
    if (!t || t.status !== 'awaiting') return;
    clearTurnTimers(t);
    t.status = 'answered';
  };

  /** A reply now on screen, remembered with the turn it answered. */
  const rememberReply = (text, forTurn = turn) => {
    const norm = normText(text);
    if (!norm) return;
    const now = Date.now();
    recentReplies = [
      ...recentReplies.filter((r) => now - r.at < RECENT_REPLY_MS),
      { norm, at: now, turnId: forTurn ? forTurn.id : null },
    ].slice(-RECENT_REPLY_MAX);
  };

  const isKnownReply = (text) => {
    const norm = normText(text);
    if (!norm) return false;
    const now = Date.now();
    return recentReplies.some((r) => now - r.at < RECENT_REPLY_MS && sameUtterance(r.norm, norm));
  };

  /**
   * Whether finished agent speech is a reply already on screen being read out.
   *
   * Scoped to turns. Speech that began after the current question was asked can
   * only be reading THIS turn's replies, so a spoken-only line the backend
   * repeats word for word (its fixed error sentences) still answers a retried
   * question. Speech that began before it may be the previous answer still
   * playing, so every recent reply counts. With no turn, every recent reply counts.
   */
  const isSpokenReply = (text, t, startedAt) => {
    const norm = normText(text);
    if (!norm) return false;
    const now = Date.now();
    const anyTurn = !t || !(startedAt >= t.startedAt);
    return recentReplies.some((r) => now - r.at < RECENT_REPLY_MS
      && (anyTurn || r.turnId === t.id)
      && spokenPartOf(r.norm, norm));
  };

  /* ── Sending ── */

  const send = async (q, via) => {
    const t = beginTurn(q, via);
    try {
      await sendChat(frameFor(q, { voice: via === 'voice' }));
    } catch (err) {
      if (!alive || turn !== t || t.status !== 'awaiting') return;
      clearTurnTimers(t);
      t.status = 'unsent';
      syncTurn();
      if (err?.code === 'not_connected') {
        /* The room went away between the check and the send; wait for it. */
        enqueue(q, via);
        return;
      }
      console.warn('[copilot] send failed', err?.message || err);
      say('error', `Copilot failed: ${err?.message || 'the question could not be sent'}`);
    }
  };

  /**
   * Hold a question until the room is ready. One slot: a second question asked
   * while still disconnected replaces the first, since the reader has moved on —
   * and, as with a newer question on a live room, only the newest is sent. The
   * one it replaced is never dropped silently: it becomes a Retry card at once.
   */
  const enqueue = (q, via) => {
    const replaced = queued;
    if (replaced) {
      cancel(replaced.timer);
      say('retry', null, { title: REPLACED_TITLE, question: replaced.text });
    }
    const entry = { text: q, via, at: Date.now(), timer: null };
    entry.timer = later(() => {
      if (queued !== entry) return;
      queued = null;
      say('retry', null, { title: NOT_SENT_TITLE, question: q });
    }, QUEUE_TTL_MS);
    queued = entry;

    const c = state.connection;
    if (halted) {
      say('error', haltText || UNAVAILABLE_TEXT, { actionId: ACTION_RECONNECT });
    } else if (c === 'failed' || c === 'disconnected') {
      /* Asking is a fresh start: with the retry budget reset, an attempt that
         fails now schedules the next one instead of giving up a second time
         under the error just posted. */
      attempts = 0;
      failNoticeShown = false;
      /* Taking the room back from another tab is not a failure to connect. */
      if (displaced) say('notice', CONNECTING_TEXT);
      else say('error', UNAVAILABLE_TEXT);
      reconnectNow('ask');
    } else if (c === 'retrying') {
      /* The same fresh start as above: asking during the last backoff wait
         would otherwise get a single attempt before giving up. */
      attempts = 0;
      if (!replaced) say('notice', CONNECTING_TEXT);
      reconnectNow('ask');
    } else if (c === 'waiting_agent' && state.agentStalled) {
      say('error', UNAVAILABLE_TEXT, { actionId: ACTION_RECONNECT });
    } else if (!replaced) {
      say('notice', CONNECTING_TEXT);
    }
  };

  const flushQueue = () => {
    const entry = queued;
    if (!entry) return;
    queued = null;
    cancel(entry.timer);
    if (Date.now() - entry.at > QUEUE_TTL_MS) return;
    send(entry.text, entry.via);
  };

  const ask = async (text, opts) => {
    const q = String(text ?? '').trim();
    if (!q || !alive) return;
    /* A report typed or passed here by mistake is still a report: silent. */
    if (safeIsReport(q)) {
      await report(q);
      return;
    }
    const via = opts && opts.via === 'voice' ? 'voice' : 'text';
    commit({ lastVia: via, silenced: false });
    addMessage({ from: 'usr', text: q, via });
    if (state.connection !== 'ready') {
      enqueue(q, via);
      return;
    }
    await send(q, via);
  };

  /** Take a report out of the outbox, answering whoever sent it. */
  const settleReport = (entry, delivered) => {
    const at = outbox.indexOf(entry);
    if (at === -1) return;
    outbox.splice(at, 1);
    cancel(entry.timer);
    if (!delivered) {
      console.warn('[copilot] report not delivered', entry.body.split('\n')[0]);
      /* The agent is waiting for exactly this line; a turn that reads as
         unanswered is usually explained by one of these. */
      logAgentApp('report dropped', { text: entry.body });
    }
    entry.resolve(delivered);
  };

  /**
   * Send held reports, oldest first, while the room is ready. Stops at the first
   * failure and leaves the rest held: the next join flushes them, and a send
   * that failed on a room that is still up is tried again shortly. Each frame
   * is built when it goes out, so it carries the screen as it is then.
   */
  const flushOutbox = async () => {
    outboxTimer = cancel(outboxTimer);
    if (flushing || !alive) return;
    flushing = true;
    const myGen = gen;
    try {
      while (outbox.length && alive && myGen === gen && state.connection === 'ready') {
        const entry = outbox[0];
        let sent = false;
        try {
          // eslint-disable-next-line no-await-in-loop -- reports go out one at a time, in order.
          sent = await trySendChat(frameFor(entry.body));
        } catch {
          sent = false;
        }
        if (!alive || myGen !== gen) return;
        if (!sent) {
          if (state.connection === 'ready') outboxTimer = later(flushOutbox, REPORT_RETRY_MS);
          return;
        }
        debug('copilot → report', { line: entry.body.split('\n')[0] });
        settleReport(entry, true);
      }
    } finally {
      if (myGen === gen) flushing = false;
    }
  };

  /**
   * An `[api_result]` report: silent, carrying fresh context. It never opens a
   * turn — the agent's reply to it is an ordinary envelope — and it is only sent
   * into a room whose agent is ready, because a line sent to an empty room is
   * accepted and lost.
   *
   * A report that cannot go out now is held in the outbox until the room is
   * back (`REPORT_HOLD_MS`), rather than retried on a timer that gives up while
   * the reconnect is still under way: the agent is waiting for exactly this one
   * line. Resolves true once sent, false when it was dropped.
   */
  const report = (text) => {
    const body = String(text ?? '').trim();
    if (!body || !alive) return Promise.resolve(false);
    return new Promise((resolve) => {
      const entry = { body, resolve, timer: null };
      entry.timer = later(() => {
        entry.timer = null;
        settleReport(entry, false);
      }, REPORT_HOLD_MS);
      outbox.push(entry);
      while (outbox.length > REPORT_OUTBOX_MAX) settleReport(outbox[0], false);
      flushOutbox();
    });
  };

  /* ── Confirmation cards ── */

  /**
   * Close a confirmation card. Resolves true for Run, 'expired' when nobody
   * answered in time (so the report can say so instead of blaming the reader),
   * and false for Cancel.
   */
  const settleConfirm = (id, yes, status) => {
    const entry = confirms.get(id);
    if (!entry) return;
    confirms.delete(id);
    cancel(entry.timer);
    updateMessage(id, { card: { status } });
    commit({ pendingConfirms: confirms.size });
    let answer = false;
    if (yes) answer = true;
    else if (status === 'expired') answer = 'expired';
    entry.resolve(answer);
  };

  const confirm = (request) => new Promise((resolve) => {
    if (!alive) {
      resolve(false);
      return;
    }
    const { title, detail, actionId, inputs } = request && typeof request === 'object' ? request : {};
    const id = addMessage({
      from: 'sys',
      kind: 'confirm',
      card: {
        title: title || 'Run this action?',
        ...(detail ? { detail } : null),
        status: 'confirm',
        ...(actionId ? { actionId } : null),
        ...(inputs && typeof inputs === 'object' && Object.keys(inputs).length ? { inputs } : null),
      },
    });
    const timer = later(() => settleConfirm(id, false, 'expired'), CONFIRM_TTL_MS);
    confirms.set(id, { resolve, timer });
    commit({ pendingConfirms: confirms.size });
  });

  /* 'done' on a confirmation card means "confirmed": the run itself is tracked on its own card. */
  const respond = (messageId, yes) => settleConfirm(messageId, Boolean(yes), yes ? 'done' : 'cancelled');

  /* ── Envelopes and replies ── */

  /**
   * Put a reply on screen. When this turn already has a stand-in line — a
   * plain chat reply, or the spoken transcript shown because the envelope was
   * late — the reply takes that line over in place, so the reader never sees
   * the same answer twice.
   */
  const showReply = (text) => {
    const t = turn;
    const takeover = t && t.lineId && t.lineKind !== 'reply'
      && Date.now() - t.lineAt < TAKEOVER_MS && findMessage(t.lineId);
    if (takeover) {
      updateMessage(t.lineId, { text, streaming: false, ok: true });
    } else {
      const id = addMessage({ from: 'bot', kind: 'text', text, reveal: true, ok: true });
      if (t && !t.lineId) {
        t.lineId = id;
        t.lineAt = Date.now();
      }
    }
    if (t) {
      t.replied = true;
      t.repliedAt = Date.now();
      t.readSegment = null;
      t.lineKind = 'reply';
    }
    rememberReply(text);
  };

  const handOff = (handoff) => {
    const target = handoff?.target;
    let agent = null;
    try {
      agent = normalizeAgentType(target);
    } catch {
      agent = null;
    }
    debug('copilot · handoff', { target, agent, subject: handoff?.subject || null });
    if (!agent) return;
    /* A handoff to the research analyst (trade_agent_enhanced → stock_research_web)
       names a stock: its own web token, minted for that stock, and its room. With
       no stock named, the one already open stays, else the handoff is refused,
       as the worker refuses a target it cannot reach. */
    if (LIVEKIT_WEB_TOKENS[agent]?.subject) {
      const subject = handoff?.subject || (agent === state.activeAgent ? state.agentSubject : null);
      if (!subject) {
        debug('copilot · handoff to a per-stock agent with no stock; ignored', { agent });
        return;
      }
      /* The window the fund analyst is on stays, when the handoff is to where the reader already is. */
      const keep = agent === state.activeAgent && subject === state.agentSubject ? state.agentOptions : null;
      switchAgent(agent, subject, keep);
      return;
    }
    /* Nothing is shown: the reader sees one assistant, whichever specialist
       answers. The agent is still tracked so a reconnect re-mints for the one
       answering now, not for Trading — and leaving a per-stock agent's room
       for a shared one is a move back to the shared room. */
    if (agent !== state.activeAgent) switchAgent(agent, null);
  };

  /**
   * One envelope, in the contract's order: handoff, reply, then the executor
   * (navigation, commands). A handoff alone keeps the turn waiting for the
   * receiving agent; a reply or any command answers it. The turn reads
   * "answering" while commands run, capped, since the executor may be waiting
   * on a page.
   */
  const onEnvelope = (payload) => {
    if (!payload || typeof payload !== 'object') return;
    const now = Date.now();
    envelopesSeen.forEach((at, k) => {
      if (now - at >= ENVELOPE_DEDUPE_MS) envelopesSeen.delete(k);
    });
    let key = null;
    try {
      key = envelopeKey(payload);
    } catch {
      key = null;
    }
    if (key) {
      if (envelopesSeen.has(key)) {
        debug('copilot · duplicate envelope dropped', { reply: typeof payload.reply === 'string' ? payload.reply.slice(0, 60) : '' });
        logAgentApp('envelope dropped', { why: 'duplicate', ...payload });
        return;
      }
      envelopesSeen.set(key, now);
    }

    let env = null;
    try {
      env = normalizeEnvelope(payload);
    } catch (err) {
      console.warn('[copilot] unreadable envelope', err);
      logAgentApp('envelope dropped', { why: 'unreadable', error: err?.message || String(err), ...payload });
      return;
    }
    if (!env || env.empty) {
      debug('copilot · empty envelope ignored', {});
      logAgentApp('envelope dropped', { why: 'empty', ...payload });
      return;
    }

    /* The agent's answer as this app understands it: the reply it will show,
       the screen it will open, the commands it will run. Read beside the raw
       envelope logged by the transport, this is where a command the agent
       sent but the app never ran becomes visible. */
    logAgentIn('envelope.parsed', {
      reply: env.reply || '',
      navigate: env.navigate || null,
      handoff: env.handoff || null,
      commands: env.commands || [],
    });

    if (env.handoff) handOff(env.handoff);

    /* The research analyst's events, for the session tab on show (it re-reads
       the session and opens a new revision); a line here says where to look,
       since the approval itself is given on that tab, not in the chat. */
    if (env.analystEvents?.length) {
      /* The session the events name themselves first; the assistant's own is the last resort, and may be stale. */
      const tagged = env.analystEvents.map((ev) => Number(ev?.session_id ?? ev?.sessionId) || null).find(Boolean);
      commit({
        analystSeq: state.analystSeq + 1,
        analystEvents: { sessionId: tagged || env.analystSessionId || state.analystSessionId, events: env.analystEvents, at: Date.now() },
      });
      env.analystEvents.forEach((ev) => {
        const type = String(ev?.type || '').toUpperCase();
        const what = String(ev?.action || ev?.preview?.action || '').replace(/_/g, ' ').trim();
        if (type === 'CONFIRMATION_REQUIRED') {
          addMessage({ from: 'bot', kind: 'text', ok: true, text: `The analyst proposes to ${what || 'take an action'} — approve or decline it in the research session tab.` });
        } else if (type === 'REPORT_UPDATED') {
          /* The revision's number in its session ("revision 3"), as the analyst says it — not its row id. */
          const n = ev?.revision_number ?? ev?.revisionNumber;
          addMessage({ from: 'bot', kind: 'text', ok: true, text: `${n != null ? `Revision ${n}` : 'A new revision'} of the report is written — it is open in the research session tab.` });
        }
      });
    }

    /* The answer's data for the screens: the picks and trades the reply
       quotes, kept where Ranked ideas and the trade screens read them. */
    if (env.payload) {
      store.dispatch(setAgentPayload({ ...env.payload, reply: env.reply || '', at: Date.now() }));
      debug('copilot · payload kept', { stocks: env.payload.suggestedStocks?.length || 0, trades: env.payload.stockTrades?.trades?.length || 0 });
    }

    const commands = Array.isArray(env.commands) ? env.commands : [];
    const acts = commands.length > 0 || Boolean(env.navigate);
    /* A handoff's pre-publish can carry the phone app's navigation; that is not
       an answer. */
    const settles = Boolean(env.reply) || commands.length > 0 || (Boolean(env.navigate) && !env.handoff);

    const token = {};
    if (settles) {
      answered(turn);
      answering.add(token);
      syncTurn();
    }
    if (env.reply) showReply(env.reply);
    if (!acts) {
      if (answering.delete(token)) syncTurn();
      return;
    }

    const myGen = gen;
    let grace = null;
    const done = () => {
      grace = cancel(grace);
      if (myGen !== gen || !answering.delete(token)) return;
      syncTurn();
    };
    if (settles) grace = later(done, ANSWER_GRACE_MS);
    Promise.resolve()
      .then(() => executeEnvelope(env))
      .then((outcome) => {
        debug('copilot · envelope executed', outcome || {});
        logAgentApp('executed', { ...(outcome || {}), commands: env.commands || [] });
      })
      .catch((err) => {
        console.error('[copilot] envelope execution failed', err);
        logAgentApp('execution failed', { error: err?.message || String(err), commands: env.commands || [] });
      })
      .finally(done);
  };

  /** A plain `lk.chat` reply from the agent — typed rather than published as an envelope. */
  const onChat = ({ id, text, final }) => {
    if (typeof text !== 'string') return;
    const lineKey = `chat-${id ?? lineId('line')}`;
    const t = turn;
    /* Taken over by the envelope's reply: the rest of this stream is a copy. */
    if (t && t.lineId === lineKey && t.lineKind === 'reply') {
      if (final && answering.delete(lineKey)) syncTurn();
      return;
    }
    if (!findMessage(lineKey)) {
      if (!text.trim() || isKnownReply(text)) return;
      addMessage({
        id: lineKey, from: 'bot', kind: 'text', text, ok: true, streaming: !final, reveal: Boolean(final),
      });
      if (t && !t.replied && !t.lineId) {
        t.lineId = lineKey;
        t.lineKind = 'chat';
        t.lineAt = Date.now();
      }
      answered(t);
      if (!final) answering.add(lineKey);
    } else {
      updateMessage(lineKey, { text, streaming: !final });
    }
    if (final) {
      answering.delete(lineKey);
      rememberReply(text);
      if (t && t.lineId === lineKey) {
        t.replied = true;
        t.repliedAt = Date.now();
        t.readSegment = null;
      }
    }
    syncTurn();
  };

  /**
   * The agent speaking. Partials drive the live caption; a final with no
   * envelope behind it becomes the reply after a short grace — the backend
   * speaks some error replies, and a human advisor's messages, without
   * publishing anything else.
   *
   * A final is the turn's reply read aloud when it started right after that
   * reply went up; its words differ (numbers, markdown), so timing decides —
   * but for ONE segment per reply, and never after a line that was itself shown
   * from speech: a second spoken line soon after the first (an advisor's two
   * quick messages, an error after a reply) is new. Otherwise the words must be
   * a reply of this turn (`isSpokenReply`).
   */
  const onAgentTranscript = ({ id, text, final }) => {
    const words = typeof text === 'string' ? text : '';
    const key = String(id || words);
    const now = Date.now();
    if (!segmentStarts.has(key)) {
      if (segmentStarts.size >= 50) segmentStarts.clear();
      segmentStarts.set(key, now);
    }
    if (!final) {
      /* Speech nobody can hear, repeating a reply already on screen, would
         only be the same sentence twice in grey. */
      setCaption({ agent: !isAudible(state) && isKnownReply(words) ? '' : words });
      return;
    }
    const startedAt = segmentStarts.get(key);
    segmentStarts.delete(key);
    setCaption({ agent: '' });
    if (!words.trim()) return;

    const t = turn;
    const readAloud = () => {
      if (!t || !t.replied || t.lineKind === 'fallback') return false;
      if (t.readSegment != null && t.readSegment !== key) return false;
      if (!(startedAt - t.repliedAt < REPLY_SPEECH_WINDOW_MS)) return false;
      t.readSegment = key;
      return true;
    };
    const known = () => readAloud() || isSpokenReply(words, t, startedAt);
    if (fallbackTimers.has(key) || known()) return;
    fallbackTimers.set(key, later(() => {
      fallbackTimers.delete(key);
      if (known()) return;
      const lineKey = addMessage({ from: 'bot', kind: 'text', text: words, reveal: true, ok: true });
      rememberReply(words, t);
      /* Only a turn still without an answer adopts the line; an answered one
         keeps its own reply as the line a late envelope would replace. */
      if (t && !t.replied && turn === t) {
        t.replied = true;
        t.repliedAt = Date.now();
        t.readSegment = key;
        t.lineId = lineKey;
        t.lineKind = 'fallback';
        t.lineAt = Date.now();
        answered(t);
        syncTurn();
      }
    }, FALLBACK_DELAY_MS));
  };

  const pruneHeard = (now) => {
    heardIds.forEach((at, k) => {
      if (now - at >= TRANSCRIPT_ID_TTL_MS) heardIds.delete(k);
    });
    heardTexts = heardTexts.filter((h) => now - h.at < TRANSCRIPT_DEDUPE_MS);
  };

  /**
   * The reader speaking. Partials are the caption. A final becomes the
   * question — once, whichever copy (data packet or stream) lands first.
   */
  const onUserTranscript = ({ id, text, final, source }) => {
    const words = typeof text === 'string' ? text.trim() : '';
    const now = Date.now();
    /* A final can trail the mic switching off by a moment and is still what
       was said; anything else with the mic off is not ours to act on. */
    if (state.mic !== 'on' && !(final && now - micOffAt < MIC_GRACE_MS)) return;
    noteVoiceActivity();
    pruneHeard(now);
    const norm = normText(words);
    const idKey = id == null ? null : String(id);

    if (!final) {
      /* A partial of an utterance already committed — late, or from the other
         source — must not bring its caption back. */
      if (idKey && heardIds.has(idKey)) return;
      if (norm && heardTexts.some((h) => now - h.at < MIC_GRACE_MS && h.norm.startsWith(norm))) return;
      setCaption({ user: words });
      return;
    }

    setCaption({ user: '' });
    if (!norm) return;
    if (idKey && heardIds.has(idKey)) return;
    if (idKey) heardIds.set(idKey, now);
    /* The same words from the same source inside the window are a repeat. From
       the OTHER source they are the same utterance even when one copy was cut
       into segments differently, so a prefix either way counts too. */
    if (heardTexts.some((h) => h.norm === norm || (h.source !== source && sameUtterance(h.norm, norm)))) return;
    heardTexts.push({ norm, at: now, source });
    debug('copilot · heard', { source, text: words });
    ask(words, { via: 'voice' });
  };

  const onSystem = (payload) => {
    if (payload?.type !== 'over_budget') {
      debug('copilot · system notice', payload || {});
      return;
    }
    /* The backend disconnects right after this. Reconnecting on our own would
       only be refused again, so it waits for the reader to ask. */
    halted = true;
    retryTimer = cancel(retryTimer);
    syncAgentWait();
    if (turn && turn.status === 'awaiting') {
      clearTurnTimers(turn);
      turn.status = 'halted';
      syncTurn();
    }
    const message = typeof payload.message === 'string' && payload.message.trim()
      ? payload.message.trim()
      : OVER_BUDGET_TEXT;
    haltText = message;
    say('error', message, { actionId: ACTION_RECONNECT });
  };

  /* ── Room status ── */

  const onDisconnected = (reason) => {
    agentPresent = false;
    userActive = false;
    idleTimer = cancel(idleTimer);
    if (state.mic === 'on' || state.mic === 'requesting') micOffAt = Date.now();
    /* The mic intent survives: it is restored when the room is back. */
    commit({
      agentState: null,
      agentActive: false,
      caption: NO_CAPTION,
      mic: state.mic === 'blocked' || state.mic === 'unavailable' ? state.mic : 'off',
    });
    answering.clear();
    segmentStarts.clear();

    const intentional = reason === 'client';
    if (intentional && (expectingDisconnect || minting)) {
      syncTurn();
      return;
    }
    /* Someone joined as this account, but no tab of this app says it did — a
       connection left over from this tab (a hot reload, a join that outlived
       its timeout) or a server-side hiccup. Telling the reader to close a tab
       that does not exist helps nobody: treat it as a drop and rejoin, once. */
    if (reason === 'duplicate_identity'
      && Date.now() - otherTabJoinedAt > ELSEWHERE_WINDOW_MS
      && Date.now() - strayRejoinAt > STRAY_REJOIN_MS) {
      strayRejoinAt = Date.now();
      debug('copilot · duplicate identity with no other tab joining; rejoining');
      giveUp(turn, LOST_TITLE);
      syncTurn();
      reconnectNow('duplicate');
      return;
    }
    if (reason === 'duplicate_identity') {
      /* Another tab or device joined as this account and LiveKit closed this
         one. Rejoining on the backoff would take the room back and close the
         other tab, which would then do the same — two tabs trading one room
         for as long as both are open. This side waits for the reader instead:
         returning to the tab, asking, or pressing Reconnect takes it over. */
      displaced = true;
      giveUp(turn, LOST_TITLE);
      retryTimer = cancel(retryTimer);
      syncTurn();
      setConnection('disconnected');
      /* One notice however many times it happens, at the bottom where it is seen. */
      setMessages(state.messages.filter((m) => !(m.from === 'sys' && m.text === ELSEWHERE_TEXT)));
      say('notice', ELSEWHERE_TEXT, { actionId: ACTION_RECONNECT });
      return;
    }
    /* Whatever the agent was about to say went with the room. */
    if (!halted) giveUp(turn, LOST_TITLE);
    syncTurn();
    if (intentional || halted) {
      setConnection('disconnected');
      return;
    }
    if (minting) return;
    scheduleRetry();
  };

  const onStatus = ({ status, reason }) => {
    switch (status) {
      case 'connecting':
        setConnection('connecting');
        return;
      case 'connected':
        setConnection(agentUsable() ? 'ready' : 'waiting_agent');
        return;
      case 'reconnecting':
        setConnection('reconnecting');
        return;
      case 'disconnected':
        onDisconnected(reason);
        return;
      default:
    }
  };

  /**
   * The agent joined or left. A join is not readiness: the worker joins before
   * its session starts, and 'ready' waits for the state it publishes once it
   * has (`onEvent`'s 'agentState'), or for `AGENT_START_GRACE_MS`.
   */
  const onAgent = ({ present }) => {
    agentPresent = Boolean(present);
    const c = state.connection;
    if (!agentPresent && halted && (c === 'ready' || c === 'waiting_agent')) {
      leaveHaltedRoom();
      return;
    }
    if (agentPresent && c === 'waiting_agent' && agentUsable()) {
      setConnection('ready');
    } else if (!agentPresent && c === 'ready') {
      commit({ agentState: null, agentActive: false });
      setConnection('waiting_agent');
    } else {
      syncAgentWait();
    }
  };

  /* ── Voice ── */

  const armIdle = () => {
    idleTimer = cancel(idleTimer);
    if (state.mic === 'on') idleTimer = later(onMicIdle, MIC_IDLE_MS);
  };

  const noteVoiceActivity = () => {
    if (state.mic === 'on') armIdle();
  };

  /**
   * Turn the mic on or off. Each call supersedes the previous one: a result
   * that comes back after the reader already changed their mind is dropped, and
   * a mic that came on anyway is switched back off.
   */
  const switchMic = async (on) => {
    if (!alive) return false;
    micSeq += 1;
    const seq = micSeq;
    if (!on) {
      micIntent = false;
      idleTimer = cancel(idleTimer);
      if (state.mic === 'on' || state.mic === 'requesting') micOffAt = Date.now();
      commit({ mic: 'off' });
      setCaption({ user: '' });
      try {
        const res = await setMicEnabled(false);
        return Boolean(res?.ok);
      } catch {
        return false;
      }
    }

    micIntent = true;
    commit({ mic: 'requesting' });
    let res;
    try {
      res = await setMicEnabled(true);
    } catch (err) {
      res = { ok: false, code: 'error', message: err?.message };
    }
    if (!alive) return false;
    if (seq !== micSeq) {
      if (res?.ok && !micIntent) setMicEnabled(false).catch(() => {});
      return false;
    }
    if (res?.ok) {
      commit({ mic: 'on' });
      armIdle();
      return true;
    }
    switch (res?.code) {
      case 'not_connected':
        /* Intent kept: the mic comes on when the room is ready. */
        commit({ mic: 'off' });
        break;
      case 'blocked':
        micIntent = false;
        commit({ mic: 'blocked' });
        break;
      default:
        micIntent = false;
        commit({ mic: 'unavailable' });
        debug('copilot · mic unavailable', { code: res?.code, message: res?.message });
    }
    return false;
  };

  const onMicIdle = () => {
    idleTimer = null;
    if (state.mic !== 'on') return;
    /* A confirmation card is the reader's turn to act, often by voice, so it
       keeps the mic open too. */
    const active = userActive || state.agentActive || answering.size > 0 || confirms.size > 0
      || (turn && turn.status === 'awaiting')
      || state.agentState === 'speaking' || state.agentState === 'thinking';
    if (active) {
      armIdle();
      return;
    }
    /* An open mic in a quiet room picks up the office, not the reader. */
    switchMic(false);
    say('notice', MIC_PAUSED_TEXT, { actionId: ACTION_MIC });
  };

  const onMicEvent = (enabled) => {
    if (enabled) {
      if (state.mic === 'on') return;
      micIntent = true;
      commit({ mic: 'on' });
      armIdle();
      return;
    }
    if (state.mic !== 'on') return;
    micOffAt = Date.now();
    idleTimer = cancel(idleTimer);
    commit({ mic: 'off' });
    setCaption({ user: '' });
  };

  const onMediaError = ({ code, message }) => {
    debug('copilot · media error', { code, message });
    if (state.mic !== 'on' && state.mic !== 'requesting') return;
    micIntent = false;
    idleTimer = cancel(idleTimer);
    commit({ mic: code === 'blocked' ? 'blocked' : 'unavailable' });
  };

  /**
   * Must be called inside the click that wants sound: browsers only let audio
   * start from a user gesture, and the transport calls `startAudio()`
   * synchronously for that reason.
   */
  const unlockAudio = () => {
    let pending;
    try {
      pending = unlockRoomAudio();
    } catch {
      pending = false;
    }
    return Promise.resolve(pending).then((ok) => Boolean(ok), () => false);
  };

  /* ── Room events ── */

  const onEvent = (event) => {
    if (!alive || !event || typeof event.type !== 'string') return;
    try {
      switch (event.type) {
        case 'status':
          onStatus(event);
          return;
        case 'agent':
          onAgent(event);
          return;
        case 'agentState': {
          const next = AGENT_STATES.has(event.state) ? event.state : null;
          commit({ agentState: next });
          if (state.connection === 'waiting_agent' && agentUsable()) setConnection('ready');
          if (next === 'speaking' || next === 'thinking') noteVoiceActivity();
          return;
        }
        case 'envelope':
          onEnvelope(event.payload);
          return;
        case 'system':
          onSystem(event.payload);
          return;
        case 'userTranscript':
          onUserTranscript(event);
          return;
        case 'agentTranscript':
          onAgentTranscript(event);
          return;
        case 'chat':
          onChat(event);
          return;
        case 'audio':
          commit({ canPlayback: Boolean(event.canPlayback) });
          return;
        case 'mic':
          onMicEvent(Boolean(event.enabled));
          return;
        case 'speaking':
          userActive = Boolean(event.user);
          commit({ agentActive: Boolean(event.agent) });
          if (event.user || event.agent) noteVoiceActivity();
          return;
        case 'mediaError':
          onMediaError(event);
          return;
        default:
      }
    } catch (err) {
      console.error(`[copilot] could not handle ${event.type}`, err);
    }
  };

  const onVisibility = () => {
    if (!alive || displaced || typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    /* Coming back to the tab is the moment the reader will ask something. */
    const c = state.connection;
    if (c === 'disconnected' || c === 'failed' || c === 'retrying') reconnectNow('visible');
    else if (c === 'waiting_agent' && state.agentStalled) reconnect();
  };

  /* ── Public API ── */

  const retry = (messageId) => {
    if (!alive) return;
    const m = findMessage(messageId);
    if (!m) return;
    if (m.card?.actionId === ACTION_RECONNECT) {
      reconnect();
      return;
    }
    const q = typeof m.card?.question === 'string' ? m.card.question.trim() : '';
    if (!q || m.card.status === 'done') return;
    updateMessage(messageId, { card: { status: 'done' } });
    ask(q, { via: 'text' });
  };

  /** Silence the agent now and stop waiting. The reply, if it still comes, is shown. */
  const stopAgent = () => {
    if (!alive) return;
    if (turn && turn.status === 'awaiting') {
      clearTurnTimers(turn);
      turn.status = 'stopped';
    }
    answering.clear();
    commit({ silenced: true });
    setCaption({ agent: '' });
    syncTurn();
  };

  const cycleSpeaker = () => {
    if (!alive) return;
    const next = NEXT_SPEAKER[state.speaker] || 'auto';
    if (next !== 'off') unlockAudio();
    writeSpeaker(next);
    commit({ speaker: next, silenced: false });
  };

  const reconnect = () => {
    if (!alive) return;
    halted = false;
    attempts = 0;
    failNoticeShown = false;
    retryTimer = cancel(retryTimer);
    if (minting) return;
    switch (state.connection) {
      case 'ready':
      case 'connecting':
      case 'reconnecting':
        return;
      case 'waiting_agent':
        remintForAgent('user');
        return;
      default:
        mint('user');
    }
  };

  const describeScreen = (next) => {
    facts = next && typeof next === 'object' && Object.keys(next).length
      ? { pathname: location?.pathname || '/', facts: next }
      : null;
  };

  const api = Object.freeze({
    ask: (text, opts) => ask(text, opts),
    retry,
    stop: stopAgent,
    toggleMic: () => {
      const on = !(state.mic === 'on' || state.mic === 'requesting');
      if (on) unlockAudio();
      return switchMic(on);
    },
    setMic: (on) => {
      if (on) unlockAudio();
      return switchMic(Boolean(on));
    },
    cycleSpeaker,
    unlockAudio,
    respond,
    reconnect,
    describeScreen,
    openStockResearch,
    openMfResearch,
    leaveStockResearch,
    leaveResearch: leaveStockResearch,
  });

  /** What the agent's command modules may use (see `agent/runtime.js`). */
  const runtime = {
    store,
    report,
    addMessage,
    updateMessage,
    confirm,
    currentLocation: () => ({ pathname: location?.pathname || '/', search: location?.search || '' }),
  };

  /* ── Lifecycle ── */

  const open = (userId) => {
    if (alive) return;
    alive = true;
    gen += 1;
    state = freshState();
    if (publish) publish(state);
    const off = setAgentRuntime(runtime);
    uninstallRuntime = typeof off === 'function' ? off : () => setAgentRuntime(null);
    if (process.env.NODE_ENV === 'development') {
      try {
        const remove = installAgentDevtools();
        uninstallDevtools = typeof remove === 'function' ? remove : null;
      } catch (err) {
        console.warn('[copilot] devtools not installed', err);
      }
    }
    unsubscribe = onRoomEvent(onEvent);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    applyVolume();
    sessionUser = userId ?? null;
    otherTabJoinedAt = 0;
    strayRejoinAt = 0;
    try {
      tabChannel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(TAB_CHANNEL) : null;
      if (tabChannel) {
        tabChannel.onmessage = (e) => {
          if (e?.data?.type === 'joining' && e.data.user === sessionUser) otherTabJoinedAt = Date.now();
        };
      }
    } catch {
      tabChannel = null;
    }
    debug('copilot · session opened', { user: userId ?? null });
    mint('start');
  };

  /**
   * Tear everything down: sign-out, an account change, or the shell unmounting.
   * The room goes with the session — leaving it up would keep an agent session
   * running for someone who is no longer here, and leak one account's thread
   * into the next.
   */
  const close = () => {
    if (!alive) return;
    alive = false;
    gen += 1;
    if (unsubscribe) unsubscribe();
    unsubscribe = null;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    try {
      tabChannel?.close();
    } catch {
      /* Already closed. */
    }
    tabChannel = null;

    timers.forEach((id) => clearTimeout(id));
    timers.clear();
    Array.from(confirms.values()).forEach((c) => c.resolve(false));
    confirms.clear();
    fallbackTimers.clear();
    segmentStarts.clear();
    answering.clear();
    envelopesSeen.clear();
    heardIds.clear();
    heardTexts = [];
    recentReplies = [];

    turn = null;
    queued = null;
    if (opening) {
      opening.reject(new Error('The assistant closed.'));
      opening = null;
    }
    switchPending = false;
    minting = null;
    retryTimer = null;
    agentTimer = null;
    startTimer = null;
    idleTimer = null;
    outboxTimer = null;
    flushing = false;
    /* Timers are already cleared, so each held report is answered here. */
    outbox.splice(0).forEach((entry) => entry.resolve(false));
    attempts = 0;
    agentRemintUsed = false;
    expectingDisconnect = false;
    halted = false;
    displaced = false;
    agentPresent = false;
    failNoticeShown = false;
    micIntent = false;
    micSeq += 1;
    userActive = false;
    facts = null;

    if (uninstallRuntime) uninstallRuntime();
    uninstallRuntime = null;
    /* After the runtime is gone, so the cancelled cards write nowhere and no
       request from this account reports into the next one. */
    try {
      cancelAgentRequests();
    } catch (err) {
      console.warn('[copilot] could not cancel agent requests', err);
    }
    if (uninstallDevtools) uninstallDevtools();
    uninstallDevtools = null;

    const leaving = disconnectRoom().catch(() => {});
    teardown = leaving;
    leaving.then(() => {
      if (teardown === leaving) teardown = null;
    });
  };

  const contextValue = (s) => {
    const audible = isAudible(s);
    return {
      connection: s.connection,
      agentState: s.agentState,
      turn: s.turn,
      /* After Stop the worker still reports 'thinking' until its run ends; the
         reader has stopped waiting, so the panel stops saying it is working. */
      thinking: s.turn === 'awaiting' || (!s.silenced && s.agentState === 'thinking'),
      speaking: audible && (s.agentState === 'speaking' || s.agentActive),
      listening: s.mic === 'on',
      waitingLong: s.waitingLong,
      activeAgent: s.activeAgent,
      activeAgentLabel: agentLabel(s.activeAgent),
      agentSubject: s.agentSubject,
      agentOptions: s.agentOptions,
      analystSessionId: s.analystSessionId,
      analystSeq: s.analystSeq,
      analystEvents: s.analystEvents,
      messages: s.messages,
      mic: s.mic,
      speaker: s.speaker,
      audible,
      audioLocked: audible && !s.canPlayback,
      caption: s.caption,
      available: s.connection === 'ready',
      connected: s.connection === 'ready' || s.connection === 'waiting_agent',
      /* The room stayed empty after its fresh token: surfaces offer Reconnect. */
      stalled: s.connection === 'waiting_agent' && s.agentStalled,
      /* A Run / Cancel card is waiting for the reader. */
      awaitingConfirm: s.pendingConfirms > 0,
      busy: s.turn !== 'idle',
      status: STATUS_ALIAS[s.connection] || 'connecting',
      ...api,
    };
  };

  return {
    /** Called during render: the latest publisher, Apollo client and location. */
    bind(next) {
      publish = next.publish;
      client = next.client;
      location = next.location || location;
    },
    snapshot: () => state,
    open,
    close,
    contextValue,
    /** The actions alone, stable for the life of the session. */
    api,
  };
}

/** The reducer only stores what the session published; the session is the source of truth. */
const latest = (_previous, next) => next;

export function CopilotProvider({ children }) {
  const sessionRef = useRef(null);
  if (sessionRef.current === null) sessionRef.current = createCopilotSession();
  const session = sessionRef.current;

  const [state, publish] = useReducer(latest, undefined, session.snapshot);
  const client = useApolloClient();
  /* Read at send time, never as a dependency: `useLocation` changes on every
     navigation, and a connection keyed on it would reconnect per page. */
  const location = useLocation();
  /* The room is minted for the signed-in account, so a different account is a
     different conversation: the session is closed and opened fresh. */
  const userId = useSelector((s) => s.auth?.userId ?? null);

  session.bind({ publish, client, location });

  useEffect(() => {
    /* The app loaded inside a frame of itself (a report link pointing back at
       the app) must not take the account's room from the page around it. */
    if (isFramed()) return undefined;
    session.open(userId);
    return () => session.close();
  }, [session, userId]);

  const value = useMemo(() => session.contextValue(state), [session, state]);
  return (
    <CopilotApiContext.Provider value={session.api}>
      <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
    </CopilotApiContext.Provider>
  );
}

function isFramed() {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    /* A cross-origin parent makes `top` unreadable, which is itself a frame. */
    return true;
  }
}

/**
 * What this page wants the assistant to know about it.
 *
 * The route already says which screen and which subject. This is for what the
 * route cannot know — the tab in view, the name of the thing being shown, a
 * figure the question will almost certainly be about. Keep it small: it rides
 * on every question the user asks from here.
 *
 * `name` is understood: it is attached to the subject the route identified,
 * so the agent gets "101762.IN (Bandhan Small Cap Fund)" rather than a symbol
 * it has to look up. Everything else is passed through as given.
 *
 * The effect has no dependency list on purpose. It assigns to a ref and
 * nothing more, so running it after every render is cheaper than the compare
 * that would avoid it — and it means a caller can pass a fresh object literal
 * without memoising it first. The provider keys what it stores by pathname, so
 * facts published just before or after a route change never describe the
 * wrong page.
 *
 * It reads the actions-only context, never the conversation, so publishing
 * facts does not subscribe the page to every caption word and speaking flag.
 */
export function useScreenContext(facts) {
  const actions = useContext(CopilotApiContext);
  const describe = actions?.describeScreen;
  useEffect(() => {
    describe?.(facts);
  });
  useEffect(() => () => describe?.(null), [describe]);
}

const noop = () => {};
const resolveFalse = async () => false;

/** The provider's shape with nothing behind it, for a screen rendered on its own. */
const OUTSIDE = Object.freeze({
  connection: 'idle',
  agentState: null,
  turn: 'idle',
  thinking: false,
  speaking: false,
  listening: false,
  waitingLong: false,
  activeAgent: DEFAULT_AGENT,
  activeAgentLabel: agentLabel(DEFAULT_AGENT),
  agentSubject: null,
  agentOptions: null,
  analystSessionId: null,
  analystSeq: 0,
  analystEvents: null,
  messages: Object.freeze([]),
  mic: 'off',
  speaker: 'auto',
  audible: false,
  audioLocked: false,
  caption: NO_CAPTION,
  available: false,
  connected: false,
  stalled: false,
  awaitingConfirm: false,
  busy: false,
  status: 'disconnected',
  ask: async () => {},
  retry: noop,
  stop: noop,
  toggleMic: resolveFalse,
  setMic: resolveFalse,
  cycleSpeaker: noop,
  unlockAudio: resolveFalse,
  respond: noop,
  reconnect: noop,
  describeScreen: noop,
  openStockResearch: async () => { throw new Error('The assistant is not running.'); },
  openMfResearch: async () => { throw new Error('The assistant is not running.'); },
  leaveStockResearch: noop,
  leaveResearch: noop,
});

/**
 * The assistant, from wherever it is asked.
 *
 * Any argument is accepted and ignored: what the user is looking at is resolved
 * once in the provider, from the route, rather than passed in by each surface
 * that happens to render a chat box. Outside the provider — a test, or a screen
 * mounted on its own — it answers as unavailable rather than throwing.
 */
export function useCopilot() {
  const ctx = useContext(CopilotContext);
  return ctx || OUTSIDE;
}

export { disconnectRoom };
